import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TenantService } from '../services/tenant.service';
import { AuthService } from '../services/auth.service';

/**
 * Route guard that restricts access to platform-only pages.
 * In App Mode (non-default tenants or installed PWA), navigating to guarded routes
 * redirects to the login page instead.
 *
 * Apply this guard to routes that should only be visible on the
 * default platform tenant (e.g., /tutors, /teach, /contact).
 */
export const canActivatePlatformOnly: CanActivateFn = (route, state) => {
  const tenantService = inject(TenantService);
  const router = inject(Router);

  // Platform-only routes (such as marketplace tutors directory, teach on learnbird)
  // are strictly restricted to the default platform tenant.
  if (!tenantService.isDefaultTenant() || tenantService.isAppMode()) {
    if (state.url.startsWith('/admin')) {
      return router.createUrlTree(['/admin/overview']);
    }
    return router.createUrlTree(['/auth/login']);
  }
  return true;
};

/**
 * Route guard for the landing page in App Mode.
 * Redirects unauthenticated App Mode users to the login page,
 * and authenticated App Mode users directly to their dashboard.
 * Platform (default tenant) browser users see the normal landing page.
 */
export const appModeLandingGuard: CanActivateFn = async () => {
  const tenantService = inject(TenantService);
  const router = inject(Router);
  const authService = inject(AuthService);

  if (tenantService.isAppMode()) {
    const isAuth = await authService.isAuthenticated();
    return router.createUrlTree([isAuth ? '/dashboard' : '/auth/login']);
  }
  return true;
};
